// Statements slice
import { StateCreator, StoreApi } from "zustand";
import { StatementExtended, AccountExtended } from "@/types";
import { AppStoreState } from "../appStore";
type SetState = StoreApi<AppStoreState>["setState"];
type GetState = StoreApi<AppStoreState>["getState"];

const createStatementsSlice: StateCreator<
  AppStoreState,
  [],
  [],
  Pick<
    AppStoreState,
    | "statements"
    | "selectedAccount"
    | "selectedStatement"
    | "setStatements"
    | "setAccounts"
    | "setSelectedAccount"
    | "setSelectedStatement"
    | "fetchStatements"
    | "fetchAccounts"
    | "removeStatement"
    | "loading"
  >
> = (set: SetState, get: GetState) => ({
  loading: {},
  statements: [] as StatementExtended[],
  selectedAccount: undefined,
  selectedStatement: undefined,
  setStatements: (statements?: StatementExtended[]) =>
    set({
      statements: Array.isArray(statements)
        ? (statements as StatementExtended[])
        : ([] as StatementExtended[]),
    }),
  setAccounts: (accounts: AccountExtended[] = []) =>
    set({ bankAccounts: accounts }),
  setSelectedAccount: (account: AccountExtended | undefined) =>
    set({ selectedAccount: account }),
  setSelectedStatement: (statement: StatementExtended | undefined) =>
    set({ selectedStatement: statement }),
  fetchStatements: async () => {
    const user = get().user;
    if (!user?.selectedReport?.id) {
      set({ statements: [] as StatementExtended[] });
      return;
    }
    const res = await fetch(
      `/api/statement/search?reportId=${user.selectedReport.id}`
    );
    const data = await res.json();
    // Defensive normalization
    const normalized = Array.isArray(data)
      ? (data.map((s: StatementExtended) => ({
          ...s,
          institution: s.institution ?? {
            id: "",
            createdAt: new Date(0),
            updatedAt: new Date(0),
            name: "",
            institutionId: "",
            logo: "",
            bic: "",
            transactionTotalDays: 0,
            countries: [],
            supportedPayments: [],
            supportedFeatures: [],
            identificationCodes: {},
          },
          statementLines: Array.isArray(s.statementLines)
            ? s.statementLines
            : [],
        })) as StatementExtended[])
      : [];
    set({ statements: normalized });
  },
  fetchAccounts: async () => {
    const user = get().user;
    if (!user?.selectedCompany?.id) return;
    const res = await fetch(
      `/api/bank-accounts?companyId=${user.selectedCompany.id}`
    );
    const data = await res.json();
    const accountsWithInstitution = Array.isArray(data.bankAccounts)
      ? data.bankAccounts.map((acc: AccountExtended) => ({
          ...acc,
          institution: acc.institution || {
            name: "",
            id: "",
            institutionId: "",
            createdAt: new Date(0),
            updatedAt: new Date(0),
            bic: "",
            transactionTotalDays: 0,
            countries: [],
            logo: "",
            supportedPayments: [],
            supportedFeatures: [],
            identificationCodes: {},
          },
        }))
      : [];
    set({
      bankAccounts: accountsWithInstitution,
    });
  },
  removeStatement: async (statementId: string) => {
    await fetch(`/api/statement/remove`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ statementId }),
    });
    await get().fetchStatements();
  },
  // Only statement/account-related state and actions remain
});

export default createStatementsSlice;
